import { useState, useEffect } from 'react';
import { graphqlClient } from '@/services/core/graphqlClient';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

export interface ProgramType {
  program_type_id: string;
  program_type: string;
  progrma_status: string[];
  created_datetime: string;
}

export interface UseProgramTypesResult {
  programTypes: ProgramType[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  refetch: () => void;
  filterType: string;
  searchValue: string;
  setFilterType: (value: string) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  clearSearch: () => void;
}

const GET_PROGRAM_TYPES_QUERY = `
  query GetProgramTypes($limit: Int!, $offset: Int!, $where: pep_vw_program_type_dashboard_bool_exp) {
    pep_vw_program_type_dashboard(limit: $limit, offset: $offset, where: $where, order_by: { program_type_id: desc }) {
      program_type
      progrma_status
      program_type_id
      created_datetime
    }
    pep_vw_program_type_dashboard_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const useProgramTypes = (limit: number = 50, offset: number = 0): UseProgramTypesResult => {
  const { hasuraToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [programTypes, setProgramTypes] = useState<ProgramType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filterType, setFilterType] = useState(searchParams.get('filterType') || 'program_type');
  const [searchValue, setSearchValue] = useState(searchParams.get('searchValue') || '');
  const [activeSearch, setActiveSearch] = useState({
    type: searchParams.get('filterType') || '',
    value: searchParams.get('searchValue') || ''
  });

  const fetchProgramTypes = async () => {
    if (!hasuraToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      let whereClause: any = {};
      
      if (activeSearch.type && activeSearch.value) {
        switch (activeSearch.type) {
          case 'program_type':
            whereClause = { program_type: { _ilike: `%${activeSearch.value}%` } };
            break;
          case 'created_datetime':
            whereClause = { created_datetime: { _gte: `${activeSearch.value}T00:00:00`, _lte: `${activeSearch.value}T23:59:59` } };
            break;
          case 'updated_datetime':
            whereClause = { updated_datetime: { _gte: `${activeSearch.value}T00:00:00`, _lte: `${activeSearch.value}T23:59:59` } };
            break;
          case 'progrma_status':
            const statusValues = activeSearch.value.split(',');
            whereClause = { 
              _or: statusValues.map(status => ({ 
                progrma_status: { _contains: [status.trim()] } 
              }))
            };
            break;
        }
      }

      const response = await graphqlClient.query(
        GET_PROGRAM_TYPES_QUERY,
        { limit, offset, where: whereClause },
        hasuraToken
      );

      if (response.success && response.data) {
        setProgramTypes(response.data.pep_vw_program_type_dashboard || []);
        setTotalCount(response.data.pep_vw_program_type_dashboard_aggregate?.aggregate?.count || 0);
        setError(null);
      } else {
        setError(response.errors?.[0] || 'Failed to fetch program types');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch program types');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch({ type: filterType, value: searchValue });
    const params = new URLSearchParams(searchParams);
    params.set('filterType', filterType);
    params.set('searchValue', searchValue);
    setSearchParams(params);
  };

  const clearSearch = () => {
    setFilterType('program_type');
    setSearchValue('');
    setActiveSearch({ type: '', value: '' });
    const params = new URLSearchParams(searchParams);
    params.delete('filterType');
    params.delete('searchValue');
    setSearchParams(params);
  };

  useEffect(() => {
    fetchProgramTypes();
  }, [hasuraToken, limit, offset, activeSearch]);

  return {
    programTypes,
    loading,
    error,
    totalCount,
    refetch: fetchProgramTypes,
    filterType,
    searchValue,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch
  };
};
